import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, formatEuro, formatDateTime, formatDate, formatTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, FileText, Download, Eye } from "lucide-react";

const STATUS_OPTS = [
  { value: "priced", label: "Devis généré" },
  { value: "booked", label: "Réservé" },
  { value: "to_review", label: "À étudier" },
  { value: "cancelled", label: "Annulé" },
  { value: "completed", label: "Terminé" },
];

const ANSWER_LABELS = {
  has_working_key: "Clé fonctionnelle disponible",
  has_remote: "Clé avec télécommande",
  vehicle_locked: "Véhicule verrouillé",
  has_registration: "Carte grise disponible",
  buttons_response: "Boutons répondent partiellement",
  keys_inside: "Clés dans le véhicule",
};
const ANSWER_VAL = { yes: "Oui", no: "Non", unknown: "Ne sait pas" };

export default function RequestDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  function load() { api.get(`/quotes/${id}`).then((r) => setData(r.data)); }
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-slate-500">Chargement…</div>;
  const { quote, booking } = data;
  const hasQuoteDoc = Boolean(quote.quote_number);
  const pdfUrl = hasQuoteDoc ? `${API}/public/quotes/${quote.id}/pdf` : null;

  async function setStatus(s) {
    try {
      await api.put(`/quotes/${quote.id}/status`, { status: s });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de changer le statut");
    }
  }

  function downloadPdf() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `${quote.quote_number}.pdf`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <div>
      <Link to="/app/demandes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#16324F]"><ChevronLeft className="w-4 h-4"/> Retour</Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[#16324F]">Demande #{quote.id.slice(0,8).toUpperCase()}</h1>
          <p className="mt-1 text-slate-500">Reçue le {formatDateTime(quote.created_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTS.map((o) => {
            const disabled = o.value === "priced" && !hasQuoteDoc;
            return (
              <Button
                key={o.value}
                size="sm"
                variant={quote.status === o.value ? "default" : "outline"}
                className="rounded-full"
                disabled={disabled}
                onClick={() => setStatus(o.value)}
                data-testid={`set-status-${o.value}`}
                title={disabled ? "Aucun devis n'a été généré pour cette demande" : undefined}
              >
                {o.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Devis section — only when a real quote document exists */}
      {hasQuoteDoc && (
        <section className="mt-8 kf-card p-6 border-l-4 border-[#19C3B3]" data-testid="quote-section">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase text-[#19C3B3] tracking-widest">
                <FileText className="w-4 h-4"/> Devis
              </div>
              <div className="mt-2 font-display font-extrabold text-2xl text-[#16324F]" data-testid="quote-number">{quote.quote_number}</div>
              <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <div><dt className="text-slate-500 text-xs uppercase tracking-wide">Date de création</dt><dd className="font-semibold text-[#16324F]" data-testid="quote-issued">{formatDate(quote.issued_at || quote.created_at)}</dd></div>
                <div><dt className="text-slate-500 text-xs uppercase tracking-wide">Montant TTC</dt><dd className="font-semibold text-[#16324F]" data-testid="quote-amount">{formatEuro(quote.price)}</dd></div>
                <div><dt className="text-slate-500 text-xs uppercase tracking-wide">Valide jusqu'au</dt><dd className="font-semibold text-[#16324F]" data-testid="quote-valid-until">{formatDate(quote.valid_until)}</dd></div>
              </dl>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="quote-view">
                <Button variant="outline" className="rounded-full h-10"><Eye className="w-4 h-4 mr-1.5"/> Voir le devis</Button>
              </a>
              <Button onClick={downloadPdf} className="rounded-full h-10 bg-[#16324F] hover:bg-[#0F2438]" data-testid="quote-download"><Download className="w-4 h-4 mr-1.5"/> Télécharger le PDF</Button>
            </div>
          </div>
        </section>
      )}

      {!hasQuoteDoc && (
        <section className="mt-8 rounded-2xl border border-[#FF7A59]/30 bg-[#FF7A59]/5 p-5 text-sm text-[#B84324]" data-testid="no-quote-notice">
          Aucun devis n'a été généré automatiquement pour cette demande — le prix ne peut pas être calculé avec fiabilité. À traiter manuellement.
        </section>
      )}

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase text-slate-500 tracking-widest">Véhicule</div>
            <div className="mt-2 font-display text-2xl font-bold text-[#16324F]">{quote.vehicle.brand} {quote.vehicle.model} · {quote.vehicle.year}</div>
            <div className="mt-1 text-slate-500 text-sm">
              {quote.vehicle.plate && <>Immatriculation : {quote.vehicle.plate} · </>}
              {quote.vehicle.vin && <>VIN : {quote.vehicle.vin}</>}
            </div>
          </div>

          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase text-slate-500 tracking-widest">Prestation</div>
            <div className="mt-2 font-display text-xl font-bold text-[#16324F]">{quote.service_name}</div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              {Object.entries(quote.answers || {}).map(([k, v]) => (
                <div key={k}><span className="text-slate-500">{ANSWER_LABELS[k] || k}</span> : <span className="font-semibold">{ANSWER_VAL[v] || v}</span></div>
              ))}
              <div><span className="text-slate-500">Urgence</span> : <span className="font-semibold">{quote.urgency === "urgent" ? "Urgent (jour même)" : "Standard"}</span></div>
              <div><span className="text-slate-500">Zone</span> : <span className="font-semibold">{quote.zone}</span></div>
            </div>
          </div>

          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase text-slate-500 tracking-widest">Localisation</div>
            <div className="mt-2 text-lg text-[#16324F]">{quote.location.city}, {quote.location.postal_code}</div>
            {quote.location.address && <div className="text-slate-500">{quote.location.address}</div>}
          </div>

          {booking && (
            <div className="kf-card p-6">
              <div className="text-xs font-bold uppercase text-slate-500 tracking-widest">Réservation</div>
              <div className="mt-2 font-display text-lg font-bold text-[#16324F]">{booking.reference}</div>
              <div className="text-slate-600">{formatDate(booking.slot_start)} à {formatTime(booking.slot_start)}</div>
              <Badge className="mt-2 bg-[#19C3B3]/15 text-[#0F7A70] border-0">{booking.status}</Badge>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase text-slate-500 tracking-widest">Montant</div>
            {quote.price ? (
              <div className="mt-2 font-display font-extrabold text-4xl text-[#16324F]">{formatEuro(quote.price)}</div>
            ) : (
              <div className="mt-2 text-[#B84324] font-semibold">À étudier</div>
            )}
            {quote.applied_rules?.length > 0 && (
              <ul className="mt-4 text-sm text-slate-600 space-y-1">
                <li>Base : {formatEuro(quote.base_price)}</li>
                {quote.applied_rules.map((r, i) => <li key={i}>• {r.label} : +{formatEuro(r.adjustment)}</li>)}
              </ul>
            )}
          </div>

          <div className="kf-card p-6">
            <div className="text-xs font-bold uppercase text-slate-500 tracking-widest">Client</div>
            {quote.customer ? (
              <div className="mt-2 text-sm space-y-1">
                <div className="font-semibold text-[#16324F]">{quote.customer.name}</div>
                <div>{quote.customer.phone}</div>
                <div className="text-slate-500">{quote.customer.email}</div>
              </div>
            ) : <div className="mt-2 text-slate-400 italic text-sm">Client anonyme (devis simple).</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
